import React from 'react';
import { getUniqueFieldValues } from '@flow-studio/flow-observer-core';
import { MultiSelectDropdown, type MultiSelectOption } from './MultiSelectDropdown.js';
import './LogFilterBar.css';

export interface LogFilterFieldDef<T> {
  key: string;
  label: string;
  getValue: (entry: T) => string | undefined | null;
  getLabel?: (value: string, entries: T[]) => string;
}

export interface LogFilterBarProps<T> {
  /** Full unfiltered buffer — dropdown options are always derived from this, not from the already-filtered view. */
  entries: T[];
  fields: LogFilterFieldDef<T>[];
  pending: Record<string, string[]>;
  onPendingChange: (key: string, values: string[]) => void;
  onExecute: () => void;
  onReset: () => void;
}

export function LogFilterBar<T>({ entries, fields, pending, onPendingChange, onExecute, onReset }: LogFilterBarProps<T>) {
  const activeCount = Object.values(pending).filter((v) => v.length > 0).length;

  return (
    <div className="fop-filterbar">
      {fields.map((field) => (
        <MultiSelectDropdown
          key={field.key}
          label={field.label}
          selected={pending[field.key] ?? []}
          onChange={(values) => onPendingChange(field.key, values)}
          getOptions={(): MultiSelectOption[] =>
            getUniqueFieldValues(entries, field.getValue).map((value) => ({
              value,
              label: field.getLabel ? field.getLabel(value, entries) : value,
            }))
          }
        />
      ))}
      <button type="button" className="fop-filterbar__execute" onClick={onExecute}>
        Execute
      </button>
      <button type="button" className="fop-filterbar__reset" onClick={onReset} disabled={activeCount === 0}>
        Reset filters
      </button>
      {activeCount > 0 && <span className="fop-filterbar__badge">{activeCount} active</span>}
    </div>
  );
}
